import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AdminLayout from './layouts/AdminLayout';
import StaffLayout from './layouts/StaffLayout';
import PlatformLayout from './layouts/PlatformLayout';
import AdminLoginPage from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminOrders from './pages/admin/AdminOrders';
import AdminPrinting from './pages/admin/AdminPrinting';
import AdminMenu from './pages/admin/AdminMenu';
import AdminReservations from './pages/admin/AdminReservations';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminTeam from './pages/admin/AdminTeam';
import AdminReports from './pages/admin/AdminReports';
import AdminSettings from './pages/admin/AdminSettings';
import AdminBillingRequired from './pages/admin/AdminBillingRequired';
import PlatformDashboard from './pages/platform/PlatformDashboard';
import LandingPage from './pages/LandingPage';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsOfServicePage from './pages/legal/TermsOfServicePage';
import DataDeletionPage from './pages/legal/DataDeletionPage';
import WhatsAppEmbeddedSignupCallbackPage from './pages/integrations/WhatsAppEmbeddedSignupCallbackPage';
import StorePage from './pages/store/StorePage';
import StoreQrPage from './pages/store/StoreQrPage';
import StoreOrderStatusPage from './pages/store/StoreOrderStatusPage';
import StoreArPage from './pages/store/StoreArPage';
import { useAuthStore } from './store/authStore';

const queryClient = new QueryClient();

const RequireAuth: React.FC<{
  children: React.ReactNode;
  allowedRoles: Array<'platform' | 'owner' | 'attendant' | 'manager' | 'kitchen'>;
  allowRestrictedOwner?: boolean;
}> = ({ children, allowedRoles, allowRestrictedOwner = false }) => {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated || !user) {
    return <Navigate to="/admin/login" replace />;
  }

  if (!allowedRoles.includes(user.role)) {
    const destination = user.role === 'platform' ? '/platform' : user.role === 'owner' ? '/admin' : '/staff';
    return <Navigate to={destination} replace />;
  }

  if (user.role === 'owner' && user.subscriptionRestricted && !allowRestrictedOwner) {
    return <Navigate to="/admin/assinatura" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
          <Route path="/termos-de-servico" element={<TermsOfServicePage />} />
          <Route path="/exclusao-de-dados" element={<DataDeletionPage />} />
          <Route path="/integracoes/whatsapp/callback" element={<WhatsAppEmbeddedSignupCallbackPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />

          <Route
            path="/platform"
            element={
              <RequireAuth allowedRoles={['platform']}>
                <Navigate to="/platform/visao-geral" replace />
              </RequireAuth>
            }
          />
          <Route
            path="/platform/visao-geral"
            element={
              <RequireAuth allowedRoles={['platform']}>
                <PlatformLayout>
                  <PlatformDashboard section="overview" />
                </PlatformLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/platform/estabilidade"
            element={
              <RequireAuth allowedRoles={['platform']}>
                <PlatformLayout>
                  <PlatformDashboard section="stability" />
                </PlatformLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/platform/bridge"
            element={
              <RequireAuth allowedRoles={['platform']}>
                <PlatformLayout>
                  <PlatformDashboard section="bridge" />
                </PlatformLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/platform/restaurantes"
            element={
              <RequireAuth allowedRoles={['platform']}>
                <PlatformLayout>
                  <PlatformDashboard section="restaurants" />
                </PlatformLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/platform/usuarios"
            element={
              <RequireAuth allowedRoles={['platform']}>
                <PlatformLayout>
                  <PlatformDashboard section="users" />
                </PlatformLayout>
              </RequireAuth>
            }
          />

          <Route
            path="/admin"
            element={
              <RequireAuth allowedRoles={['owner']}>
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/admin/pedidos"
            element={
              <RequireAuth allowedRoles={['owner']}>
                <AdminLayout>
                  <AdminOrders />
                </AdminLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/admin/impressao"
            element={
              <RequireAuth allowedRoles={['owner']}>
                <AdminLayout>
                  <AdminPrinting />
                </AdminLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/admin/cardapio"
            element={
              <RequireAuth allowedRoles={['owner']}>
                <AdminLayout>
                  <AdminMenu />
                </AdminLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/admin/reservas"
            element={
              <RequireAuth allowedRoles={['owner']}>
                <AdminLayout>
                  <AdminReservations />
                </AdminLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/admin/clientes"
            element={
              <RequireAuth allowedRoles={['owner']}>
                <AdminLayout>
                  <AdminCustomers />
                </AdminLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/admin/equipe"
            element={
              <RequireAuth allowedRoles={['owner']}>
                <AdminLayout>
                  <AdminTeam />
                </AdminLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/admin/relatorios"
            element={
              <RequireAuth allowedRoles={['owner']}>
                <AdminLayout>
                  <AdminReports />
                </AdminLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/admin/assinatura"
            element={
              <RequireAuth allowedRoles={['owner']} allowRestrictedOwner>
                <AdminBillingRequired />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/configuracoes"
            element={
              <RequireAuth allowedRoles={['owner']} allowRestrictedOwner>
                <AdminLayout>
                  <AdminSettings />
                </AdminLayout>
              </RequireAuth>
            }
          />

          <Route
            path="/staff"
            element={
              <RequireAuth allowedRoles={['attendant']}>
                <StaffLayout>
                  <AdminOrders />
                </StaffLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/staff/reservas"
            element={
              <RequireAuth allowedRoles={['attendant']}>
                <StaffLayout>
                  <AdminReservations />
                </StaffLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/staff/cardapio"
            element={
              <RequireAuth allowedRoles={['attendant']}>
                <StaffLayout>
                  <AdminMenu />
                </StaffLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/staff/clientes"
            element={
              <RequireAuth allowedRoles={['attendant']}>
                <StaffLayout>
                  <AdminCustomers />
                </StaffLayout>
              </RequireAuth>
            }
          />

          <Route path="/store/:slug/qr" element={<StoreQrPage />} />
          <Route path="/store/:slug/ar" element={<StoreArPage />} />
          <Route path="/store/:slug/pedido/:shortId" element={<StoreOrderStatusPage />} />
          <Route path="/store/:slug" element={<StorePage />} />
          <Route path="/store/:slug/*" element={<StorePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
